import { useEffect, useRef } from "react";
import { useGoogleMaps } from "../lib/useGoogleMaps";
import { AlertCircle, KeyRound } from "lucide-react";

const STATUS_COLOR = {
  paid: "#10B981", accepted: "#3B82F6", in_progress: "#F26522",
};

/**
 * AdminLiveMap — plots pickup + drop pins for active bookings and mate current-locations.
 * Falls back to a table-only view if Maps key is missing.
 */
export default function AdminLiveMap({ bookings = [], mates = [] }) {
  const { isReady, hasKey, isLoading } = useGoogleMaps();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markers = useRef([]);

  useEffect(() => {
    if (!isReady || !mapRef.current) return;
    const google = window.google;
    if (!mapInstance.current) {
      mapInstance.current = new google.maps.Map(mapRef.current, {
        center: { lat: 18.5204, lng: 73.8567 }, zoom: 11, disableDefaultUI: false,
        mapTypeControl: false, streetViewControl: false, gestureHandling: "greedy",
      });
    }
    // Clear old markers
    markers.current.forEach((m) => m.setMap(null));
    markers.current = [];
    const bounds = new google.maps.LatLngBounds();

    bookings.forEach((b) => {
      if (b.pickup_lat && b.pickup_lng) {
        const m = new google.maps.Marker({
          position: { lat: b.pickup_lat, lng: b.pickup_lng },
          map: mapInstance.current,
          title: `${b.service_name} · ${b.customer_name}`,
          icon: { path: google.maps.SymbolPath.CIRCLE, fillColor: STATUS_COLOR[b.status] || "#333",
                  fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2, scale: 10 },
        });
        const info = new google.maps.InfoWindow({
          content: `<div style="font-family:system-ui;font-size:12px"><b>${b.service_name}</b><br/>${b.customer_name}<br/>${b.pickup_address || ""}<br/><i>${b.status}</i></div>`,
        });
        m.addListener("click", () => info.open(mapInstance.current, m));
        markers.current.push(m);
        bounds.extend({ lat: b.pickup_lat, lng: b.pickup_lng });
      }
      if (b.drop_lat && b.drop_lng) {
        const m = new google.maps.Marker({
          position: { lat: b.drop_lat, lng: b.drop_lng },
          map: mapInstance.current, title: `Drop · ${b.service_name}`,
          icon: { path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW, fillColor: "#111",
                  fillOpacity: 0.9, strokeColor: "#fff", strokeWeight: 2, scale: 5 },
        });
        markers.current.push(m);
        bounds.extend({ lat: b.drop_lat, lng: b.drop_lng });
      }
    });
    mates.forEach((m) => {
      if (!m.current_lat || !m.current_lng) return;
      const mk = new google.maps.Marker({
        position: { lat: m.current_lat, lng: m.current_lng },
        map: mapInstance.current, title: `Mate · ${m.name}`,
        icon: { path: "M12 2C7.03 2 3 6.03 3 11c0 5.25 7 11 9 11s9-5.75 9-11c0-4.97-4.03-9-9-9zm0 12a3 3 0 110-6 3 3 0 010 6z",
                fillColor: "#F26522", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 1.5, scale: 1.2, anchor: new google.maps.Point(12, 22) },
      });
      markers.current.push(mk);
      bounds.extend({ lat: m.current_lat, lng: m.current_lng });
    });
    if (markers.current.length > 0) {
      mapInstance.current.fitBounds(bounds, 60);
      if (mapInstance.current.getZoom() > 15) mapInstance.current.setZoom(15);
    }
  }, [isReady, bookings, mates]);

  if (isLoading) {
    return <div className="rounded-2xl border border-black/10 bg-neutral-50 h-[420px] grid place-items-center text-neutral-400">Loading map…</div>;
  }
  if (!hasKey) {
    return (
      <div className="rounded-2xl border border-dashed border-amber-300 bg-amber-50 p-6 text-sm text-amber-800">
        <div className="flex gap-2 items-start">
          <KeyRound className="w-4 h-4 mt-0.5" />
          <div>
            <b>Google Maps key not configured.</b> Add <code>GOOGLE_MAPS_API_KEY</code> to <code>backend/.env</code> to enable the live map.
            All booking locations are still captured — see the table below.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div ref={mapRef} className="rounded-2xl border border-black/10 overflow-hidden" style={{ height: 480, width: "100%" }} data-testid="admin-live-map-canvas" />
      <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-neutral-600">
        <span className="chip"><span className="w-2 h-2 rounded-full inline-block" style={{ background: "#10B981" }} /> Paid</span>
        <span className="chip"><span className="w-2 h-2 rounded-full inline-block" style={{ background: "#3B82F6" }} /> Accepted</span>
        <span className="chip"><span className="w-2 h-2 rounded-full inline-block" style={{ background: "#F26522" }} /> In progress</span>
        <span className="chip"><span className="w-2 h-2 rounded-full inline-block" style={{ background: "#111" }} /> Drop location</span>
      </div>
      {bookings.length === 0 && mates.length === 0 && (
        <div className="mt-3 rounded-xl border border-dashed border-black/10 bg-white p-4 text-xs text-neutral-500 flex gap-2 items-center">
          <AlertCircle className="w-3.5 h-3.5" /> No active bookings with location data yet.
        </div>
      )}
    </div>
  );
}
